import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Stack,
  Box,
  Checkbox,
  FormControlLabel,
  Collapse,
  Divider,
  Typography,
  Switch,
  IconButton,
  Button,
} from '@mui/material';
import { ChevronRight, ExpandMore } from '@mui/icons-material';
import {
  extractFleetHealthCategories,
  extractServiceGroupFieldName,
  FleetHealthFilters,
} from '@/utils/fleetHealthUtils';
import { abyssColors } from '@/theme/colors';

interface CategoryConfig {
  id: string;
  name: string;
  enabled: boolean;
  expanded: boolean;
  riLevels: Set<string>; // Selected ri levels
  availableRiLevels: Set<string>; // Available ri levels from data
}

interface PlatformFleetHealthModalProps {
  open: boolean;
  onClose: () => void;
  platforms: Array<{ fleetHealthFilters?: FleetHealthFilters }>;
  onApplyFilters: (selectedFilters: {
    categories: Record<
      string,
      {
        riLevels: string[];
      }
    >;
  }) => void;
}

const formatRiLevel = (level: string): string => {
  return level.toUpperCase().replace('RI', 'Ri');
};

export const PlatformFleetHealthModal: React.FC<PlatformFleetHealthModalProps> = ({
  open,
  onClose,
  platforms,
  onApplyFilters,
}) => {
  const [categoryConfigs, setCategoryConfigs] = useState<CategoryConfig[]>([]);
  const hasInitialized = useRef(false);

  // Extract all categories from platforms
  const extractedData = useMemo(() => {
    return extractFleetHealthCategories(platforms);
  }, [platforms]);

  // Extract the fieldName for service group label (e.g., "criticality" -> "Criticality")
  const serviceGroupFieldLabel = useMemo(() => {
    return extractServiceGroupFieldName(platforms) ?? 'service group';
  }, [platforms]);

  // Initialize configs from extracted categories
  const initialConfigs = useMemo(() => {
    return extractedData.categories.map((category) => ({
      id: category.id,
      name: category.name,
      enabled: true, // All enabled by default
      expanded: true, // All expanded by default
      riLevels: new Set(category.availableRiLevels), // All RI levels selected by default
      availableRiLevels: category.availableRiLevels,
    }));
  }, [extractedData]);

  // Initialize configs only once when component mounts
  useEffect(() => {
    if (!hasInitialized.current && initialConfigs.length > 0) {
      setCategoryConfigs(initialConfigs);
      hasInitialized.current = true;
    }
  }, [initialConfigs]);

  const updateConfig = useCallback((id: string, updates: Partial<CategoryConfig>) => {
    setCategoryConfigs((prev) =>
      prev.map((config) => (config.id === id ? { ...config, ...updates } : config))
    );
  }, []);

  const handleToggleCategory = useCallback(
    (categoryId: string) => {
      const config = categoryConfigs.find((c) => c.id === categoryId);
      if (!config) return;

      const category = extractedData.categoryMap.get(categoryId);
      const isServiceGroup = category?.type === 'service_group';

      if (!config.enabled) {
        // Turning ON
        if (isServiceGroup) {
          // Service groups: select all RI levels automatically (no dropdown)
          updateConfig(categoryId, {
            enabled: true,
            riLevels: new Set(config.availableRiLevels),
          });
        } else {
          // Deployment types: expand and select all RI levels
          updateConfig(categoryId, {
            enabled: true,
            expanded: true,
            riLevels: new Set(config.availableRiLevels),
          });
        }
      } else {
        // Turning OFF: close dropdown and clear all RI levels
        updateConfig(categoryId, {
          enabled: false,
          expanded: false,
          riLevels: new Set(),
        });
      }
    },
    [updateConfig, categoryConfigs, extractedData.categoryMap]
  );

  const handleExpandToggle = useCallback(
    (categoryId: string) => {
      const config = categoryConfigs.find((c) => c.id === categoryId);
      if (!config) return;

      updateConfig(categoryId, {
        expanded: !config.expanded,
      });
    },
    [updateConfig, categoryConfigs]
  );

  const handleRiLevelToggle = useCallback(
    (categoryId: string, riLevel: string) => {
      const config = categoryConfigs.find((c) => c.id === categoryId);
      if (!config) return;

      const category = extractedData.categoryMap.get(categoryId);
      const isDeployment = category?.type === 'deployment';

      const newRiLevels = new Set(config.riLevels);
      if (newRiLevels.has(riLevel)) {
        newRiLevels.delete(riLevel);
      } else {
        newRiLevels.add(riLevel);
      }

      // If this is a deployment, also update all its child service groups
      if (isDeployment) {
        setCategoryConfigs((prev) =>
          prev.map((cfg) => {
            // Update the deployment itself
            if (cfg.id === categoryId) {
              return { ...cfg, riLevels: newRiLevels };
            }

            // Update child service groups
            const childCategory = extractedData.categoryMap.get(cfg.id);
            if (
              childCategory?.type === 'service_group' &&
              childCategory.parentDeployment === categoryId
            ) {
              const childRiLevels = new Set(cfg.riLevels);
              if (newRiLevels.has(riLevel)) {
                // Parent added this RI level, add it to child if it's available
                if (cfg.availableRiLevels.has(riLevel)) {
                  childRiLevels.add(riLevel);
                }
              } else {
                // Parent removed this RI level, remove it from child
                childRiLevels.delete(riLevel);
              }
              return { ...cfg, riLevels: childRiLevels };
            }

            return cfg;
          })
        );
      } else {
        // For service groups, just update themselves
        updateConfig(categoryId, { riLevels: newRiLevels });
      }
    },
    [categoryConfigs, updateConfig, extractedData.categoryMap, setCategoryConfigs]
  );

  const handleApply = useCallback(() => {
    const selectedFilters: {
      categories: Record<
        string,
        {
          riLevels: string[];
        }
      >;
    } = {
      categories: {},
    };

    categoryConfigs.forEach((config) => {
      if (config.enabled) {
        selectedFilters.categories[config.id] = {
          riLevels: Array.from(config.riLevels),
        };
      }
    });

    onApplyFilters(selectedFilters);
    onClose();
  }, [categoryConfigs, onApplyFilters, onClose]);

  const hasEnabledCategories = categoryConfigs.some((c) => c.enabled);

  // Group service groups by their parent deployment
  const deploymentWithServiceGroups = useMemo(() => {
    const deployments: CategoryConfig[] = [];
    const serviceGroupsByDeployment: Record<string, CategoryConfig[]> = {};

    categoryConfigs.forEach((config) => {
      const category = extractedData.categoryMap.get(config.id);
      if (category?.type === 'deployment') {
        deployments.push(config);
        serviceGroupsByDeployment[config.id] = [];
      }
    });

    // Group service groups under their parent deployments
    categoryConfigs.forEach((config) => {
      const category = extractedData.categoryMap.get(config.id);
      if (category?.type === 'service_group' && category.parentDeployment) {
        if (!serviceGroupsByDeployment[category.parentDeployment]) {
          serviceGroupsByDeployment[category.parentDeployment] = [];
        }
        serviceGroupsByDeployment[category.parentDeployment].push(config);
      }
    });

    return {
      deployments,
      serviceGroupsByDeployment,
    };
  }, [categoryConfigs, extractedData.categoryMap]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          padding: '24px',
        },
      }}
    >
      <Typography variant="h4" sx={{ mb: 2 }}>
        Filters
      </Typography>

      <DialogContent sx={{ padding: 0 }}>
        <Typography variant="h6">Select the source of data</Typography>

        <Stack>
          {/* Deployment Types with nested Service Groups */}
          {deploymentWithServiceGroups.deployments.map((deploymentConfig, index) => {
            const serviceGroups =
              deploymentWithServiceGroups.serviceGroupsByDeployment[deploymentConfig.id] || [];

            // Only show service groups for "pressure" deployment
            const hasServiceGroups =
              deploymentConfig.id.toLowerCase() === 'pressure' && serviceGroups.length > 0;

            return (
              <Box key={deploymentConfig.id}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {deploymentConfig.enabled ? (
                      <IconButton
                        size="small"
                        onClick={() => handleExpandToggle(deploymentConfig.id)}
                        sx={{ p: 0.5 }}
                      >
                        {deploymentConfig.expanded ? <ExpandMore /> : <ChevronRight />}
                      </IconButton>
                    ) : (
                      <Box sx={{ width: '28px' }} />
                    )}
                    <Typography variant="h6">{deploymentConfig.name}</Typography>
                  </Box>
                  <Switch
                    checked={deploymentConfig.enabled}
                    onChange={() => handleToggleCategory(deploymentConfig.id)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: abyssColors.secondary[600],
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: abyssColors.secondary[600],
                      },
                    }}
                  />
                </Box>

                <Collapse in={deploymentConfig.enabled && deploymentConfig.expanded}>
                  <Box sx={{ pl: 1 }}>
                    {/* RI Levels Section */}
                    <Stack>
                      {Array.from(deploymentConfig.availableRiLevels)
                        .sort()
                        .map((riLevel) => (
                          <FormControlLabel
                            key={riLevel}
                            control={
                              <Checkbox
                                checked={deploymentConfig.riLevels.has(riLevel)}
                                onChange={() => handleRiLevelToggle(deploymentConfig.id, riLevel)}
                                sx={{
                                  color: abyssColors.primary[500],
                                  padding: '5px',
                                }}
                              />
                            }
                            label={
                              <Typography variant="body2">{formatRiLevel(riLevel)}</Typography>
                            }
                            sx={{ margin: 0 }}
                          />
                        ))}
                    </Stack>

                    {/* Service Groups Section */}
                    {hasServiceGroups && (
                      <Box sx={{ my: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, pl: 1 }}>
                          Filter data by {serviceGroupFieldLabel}
                        </Typography>
                        <Stack>
                          {serviceGroups.map((serviceGroupConfig) => (
                            <FormControlLabel
                              key={serviceGroupConfig.id}
                              control={
                                <Checkbox
                                  checked={serviceGroupConfig.enabled}
                                  onChange={() => handleToggleCategory(serviceGroupConfig.id)}
                                  sx={{
                                    color: abyssColors.primary[500],
                                    padding: '5px',
                                  }}
                                />
                              }
                              label={
                                <Typography variant="body2">{serviceGroupConfig.name}</Typography>
                              }
                              sx={{ margin: 0 }}
                            />
                          ))}
                        </Stack>
                      </Box>
                    )}
                  </Box>
                </Collapse>

                {index < deploymentWithServiceGroups.deployments.length - 1 && (
                  <Divider sx={{ borderColor: abyssColors.primary[200], my: 1 }} />
                )}
              </Box>
            );
          })}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={onClose}
          variant="text"
          sx={{
            '&:hover': {
              backgroundColor: abyssColors.primary[100],
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          disabled={!hasEnabledCategories}
          sx={{
            backgroundColor: abyssColors.primary[500],
            fontSize: '1.4rem',
            fontWeight: 500,
            textTransform: 'none',
            px: 3,
            py: 1,
            borderRadius: '6px',
            '&:hover': {
              backgroundColor: abyssColors.primary[400],
            },
            '&:disabled': {
              backgroundColor: abyssColors.primary[200],
              color: abyssColors.primary[400],
            },
          }}
        >
          Save & apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};
